import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import './Navbar.css';

// Importer votre logo
import logo from '../../assets/images/logo.webp';

// Importer les icônes
import { FaUser, FaUserCog, FaHome } from 'react-icons/fa';

// Importer les hooks de contexte
import { useFavorites } from '../../context/FavoritesContext';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';

function Navbar() {
  const { favorites, toggleSidebar } = useFavorites();
  const { cartItems, toggleCart } = useCart();
  const { isLoggedIn, user, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Fermer le menu à chaque changement de page
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Bloquer le scroll du body quand le menu est ouvert
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  return (
    <>
      <header className="navbar-header">
        <nav className="navbar">
          {/* --- BOUTON BURGER (VISIBLE SUR MOBILE UNIQUEMENT) --- */}
          <button className="burger-menu" onClick={toggleMenu} aria-label="Ouvrir le menu">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M3,6H21V8H3V6M3,11H21V13H3V11M3,16H21V18H3V16Z" />
            </svg>
          </button>

          {/* --- LOGO --- */}
          <Link to="/" className="navbar-brand">
            <img src={logo} alt="Logo Couleur Sirène" className="navbar-logo" />
            <span className="brand-name">Couleur Sirène</span>
          </Link>
          
          {/* --- NAVIGATION POUR DESKTOP (VISIBLE SUR DESKTOP UNIQUEMENT) --- */}
          <div className="nav-desktop-container">
            <ul className="nav-links">
              <li><NavLink to="/">Accueil</NavLink></li>
              <li><NavLink to="/a-propos">À Propos</NavLink></li>
              <li><NavLink to="/catalogue">Catalogue</NavLink></li>
               <li><NavLink to="/galerie">Galerie</NavLink></li>             
              <li><NavLink to="/agenda">Agenda</NavLink></li>
              <li><NavLink to="/contact">Contact</NavLink></li>
              <li>
                {isLoggedIn ? (
                  user?.role === 'admin' ? (
                    <Link to="/admin" className="nav-action-button" title="Tableau de bord">
                      <FaHome className="nav-icon" />
                    </Link>
                  ) : (
                    <Link to="/mon-compte" className="nav-action-button" title="Mon Compte">
                      <FaUserCog className="nav-icon" />
                    </Link>
                  )
                ) : (
                  <Link to="/auth" className="nav-action-button" title="Connexion">
                    <FaUser className="nav-icon" />
                  </Link>
                )}
              </li>
            </ul>
            <div className="nav-social">
               <a href="https://www.instagram.com/couleur_sirene/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <svg viewBox="0 0 24 24"><path d="M7.8,2H16.2C19.4,2 22,4.6 22,7.8V16.2A5.2,5.2 0 0,1 16.2,21.4H7.8C4.6,21.4 2,18.8 2,15.6V7.8C2,4.6 4.6,2 7.8,2M7.6,4A3.6,3.6 0 0,0 4,7.6V16.4C4,18.39 5.61,20 7.6,20H16.4A3.6,3.6 0 0,0 20,16.4V7.6C20,5.61 18.39,4 16.4,4H7.6M17.25,5.5A1.25,1.25 0 0,1 18.5,6.75A1.25,1.25 0 0,1 17.25,8A1.25,1.25 0 0,1 16,6.75A1.25,1.25 0 0,1 17.25,5.5M12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9Z" /></svg>
              </a>
              <a href="https://www.facebook.com/people/Couleur-Sir%C3%A8ne/100075524093868/" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <svg viewBox="0 0 24 24"><path d="M12 2.04C6.5 2.04 2 6.53 2 12.06C2 17.06 5.66 21.21 10.44 21.96V14.96H7.9V12.06H10.44V9.81C10.44 7.31 11.93 5.96 14.22 5.96C15.31 5.96 16.45 6.15 16.45 6.15V8.62H15.19C13.95 8.62 13.56 9.39 13.56 10.18V12.06H16.34L15.89 14.96H13.56V21.96A10 10 0 0 0 22 12.06C22 6.53 17.5 2.04 12 2.04Z" /></svg>
              </a>
            </div>
          </div>

          {/* --- ACTIONS (PANIER/FAVORIS) - TOUJOURS VISIBLES --- */}
          <div className="nav-actions">
            <button className="nav-action-btn" onClick={toggleSidebar} aria-label="Ouvrir les favoris">
              <svg viewBox="0 0 24 24"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
              {favorites.length > 0 && <span className="nav-count">{favorites.length}</span>}
            </button>
            <button className="nav-action-btn" onClick={toggleCart} aria-label="Ouvrir le panier">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path></svg>
              {cartItems.length > 0 && <span className="nav-count">{cartItems.length}</span>}
            </button>
          </div>
        </nav>
      </header>

      {/* --- PANNEAU LATÉRAL MOBILE (MAINTENANT EN DEHORS DU HEADER) --- */}
      <div className={`nav-right ${isMenuOpen ? 'open' : ''}`}>
        <button className="close-btn" onClick={toggleMenu} aria-label="Fermer le menu">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
          </svg>
        </button>
        <ul className="nav-links">
          <li><NavLink to="/" onClick={toggleMenu}>Accueil</NavLink></li>
          <li><NavLink to="/a-propos" onClick={toggleMenu}>À Propos</NavLink></li>
          <li><NavLink to="/catalogue" onClick={toggleMenu}>Catalogue</NavLink></li>
          <li><NavLink to="/galerie" onClick={toggleMenu}>Galerie</NavLink></li>          
          <li><NavLink to="/agenda" onClick={toggleMenu}>Agenda</NavLink></li>
          <li><NavLink to="/contact" onClick={toggleMenu}>Contact</NavLink></li>
        </ul>
        <div className="nav-social">
          <a href="https://www.instagram.com/couleur_sirene/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
            <svg viewBox="0 0 24 24"><path d="M7.8,2H16.2C19.4,2 22,4.6 22,7.8V16.2A5.2,5.2 0 0,1 16.2,21.4H7.8C4.6,21.4 2,18.8 2,15.6V7.8C2,4.6 4.6,2 7.8,2M7.6,4A3.6,3.6 0 0,0 4,7.6V16.4C4,18.39 5.61,20 7.6,20H16.4A3.6,3.6 0 0,0 20,16.4V7.6C20,5.61 18.39,4 16.4,4H7.6M17.25,5.5A1.25,1.25 0 0,1 18.5,6.75A1.25,1.25 0 0,1 17.25,8A1.25,1.25 0 0,1 16,6.75A1.25,1.25 0 0,1 17.25,5.5M12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9Z" /></svg>
          </a>
          <a href="https://www.facebook.com/people/Couleur-Sir%C3%A8ne/100075524093868/" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
            <svg viewBox="0 0 24 24"><path d="M12 2.04C6.5 2.04 2 6.53 2 12.06C2 17.06 5.66 21.21 10.44 21.96V14.96H7.9V12.06H10.44V9.81C10.44 7.31 11.93 5.96 14.22 5.96C15.31 5.96 16.45 6.15 16.45 6.15V8.62H15.19C13.95 8.62 13.56 9.39 13.56 10.18V12.06H16.34L15.89 14.96H13.56V21.96A10 10 0 0 0 22 12.06C22 6.53 17.5 2.04 12 2.04Z" /></svg>
          </a>
        </div>
        {/* Bouton compte/connexion tout en bas */}
        <div className="nav-account-section">
          {isLoggedIn ? (
            user?.role === 'admin' ? (
              <NavLink to="/admin" onClick={toggleMenu} className="mobile-icon-link account-link admin-link">
                <FaHome className="mobile-nav-icon" />
                <span>Tableau de bord</span>
              </NavLink>
            ) : (
              <NavLink to="/mon-compte" onClick={toggleMenu} className="mobile-icon-link account-link">
                <FaUserCog className="mobile-nav-icon" />
                <span>Mon Compte</span>
              </NavLink>
            )
          ) : (
            <NavLink to="/auth" onClick={toggleMenu} className="mobile-icon-link account-link auth-link">
              <FaUser className="mobile-nav-icon" />
              <span>Connexion</span>
            </NavLink>
          )}
        </div>
      </div>

      {/* --- OVERLAY (MOBILE) --- */}
      {isMenuOpen && <div className="overlay" onClick={toggleMenu}></div>}
    </>
  );
}

export default Navbar;